import React, { useCallback, useState } from 'react';

import { Platform, ScrollView, StyleSheet } from 'react-native';

import { IBaseItem, IItem, ILegacyCartEntry } from '@rbi-ctg/menu';
import { DraggableScrollView } from 'components/draggable-scrollview';
import { transformUserSelectionsModifierToModifierSelection } from 'components/product-detail-v2/utils';
import { VisuallyHidden } from 'components/ucl/visually-hidden';
import { Box } from 'components/ui';
import { useCart } from 'hooks/use-cart';
import { useIsDesktopBreakpoint } from 'hooks/use-media-query';
import SectionHeading from 'pages/cart/section-heading';
import { CustomEventNames, EventTypes, logRBIEvent } from 'state/amplitude';
import { t } from 'state/intl/translations';
import { ProductWizardUtils } from 'state/product-wizard-v2/utils';
import { spacing } from 'styles/constants';
import { routes } from 'utils/routing';
import { transformMenuObjectToCartItem } from 'utils/wizard';

import { UpsellSectionContainer, UpsellTitle } from '../styles';
import UpsellItem from '../upsell-item';
import { UpsellSkeleton } from '../upsell.skeleton';

import { OneMenuUpsellItem } from './one-menu-upsell-item';
import { IPersonalizedUpsellProps } from './types';

export const PersonalizedUpsell: React.FC<IPersonalizedUpsellProps> = ({
  isInCart,
  items,
  loading,
}) => {
  const { addItemToCart } = useCart();

  const [orderAddedAlertMessage, setOrderAddedAlertMessage] = useState('');

  const isDesktop = useIsDesktopBreakpoint();
  const isDesktopWeb = isDesktop && Platform.OS === 'web';

  const ScrollViewComponent = isDesktopWeb ? DraggableScrollView : ScrollView;

  const handleAddToCart = useCallback(
    (item: IBaseItem) => {
      // Make sure to always get the default modifiers for the item
      const defaultSelections = ProductWizardUtils.SetDefaultSelections({
        menuObject: item as IItem,
      });
      const modifierSelections = transformUserSelectionsModifierToModifierSelection(
        defaultSelections
      );

      const { mainEntry } = transformMenuObjectToCartItem(item as IItem, {
        modifierSelections,
        pathname: `${routes.menu}/${item._type}-${item._id}`,
      });

      if (mainEntry) {
        const entry: ILegacyCartEntry = { ...mainEntry, isUpsell: true };
        addItemToCart(entry);

        const upsellItemIndex = items.findIndex(
          upsellEntry => upsellEntry.source === 'legacy' && upsellEntry.item._id === item._id
        );

        const upsellItemPosition = upsellItemIndex >= 0 ? upsellItemIndex + 1 : undefined;

        logRBIEvent({
          name: CustomEventNames.UPSELL_ADDED,
          type: EventTypes.Other,
          attributes: {
            name: entry.name,
            sanityId: entry._id,
            ...(entry.price && { price: entry.price / 100 }),
            upsellItemPosition,
          },
        });

        setOrderAddedAlertMessage(t('itemAddedToCart', { itemName: item.name?.locale }));
      }
    },
    [addItemToCart, items]
  );

  if (!items.length && !loading) {
    return null;
  }

  const title = t('addToOrderHeader');

  return (
    // negative margin on cart to show you can scroll right for more as it goes to edge of screen (or cut off on desktop web)
    <UpsellSectionContainer
      testID="upsell"
      style={isInCart && !isDesktopWeb ? styles.inCartContainer : undefined}
    >
      <Box>
        {isInCart ? (
          <SectionHeading testID="add-to-order-header">{title}</SectionHeading>
        ) : (
          <UpsellTitle>{title}</UpsellTitle>
        )}
      </Box>
      {loading && !items.length ? (
        <UpsellSkeleton />
      ) : (
        <ScrollViewComponent horizontal showsHorizontalScrollIndicator={false}>
          {items.map((entry, index) =>
            entry.source === 'one-menu' ? (
              <OneMenuUpsellItem
                key={`one-menu-${entry.item.id}`}
                isInCart={isInCart}
                item={entry.item}
                position={index + 1}
              />
            ) : (
              <UpsellItem
                key={`legacy-${entry.item._id}`}
                item={entry.item}
                addToCard={handleAddToCart}
              />
            )
          )}
        </ScrollViewComponent>
      )}
      {orderAddedAlertMessage.length > 0 && (
        <VisuallyHidden role="alert" accessibilityLabel={orderAddedAlertMessage} />
      )}
    </UpsellSectionContainer>
  );
};

const styles = StyleSheet.create({
  inCartContainer: {
    marginRight: spacing.$2 * -1,
  },
});
